<template>
    <section class="order-section">
        <header class="section-header">
            <h1 class="section-title">Zamówienia</h1>
        </header>

        <EntityFilterBar :show-search="false" v-model:search="searchQuery" v-model:filters="filters"
            v-model:sort="sortModel" :filters-schema="[
                {
                    key: 'customer_type',
                    label: 'Typ klienta',
                    options: [
                        { id: 'company', name: orderStore.formatType('company') },
                        { id: 'individual', name: orderStore.formatType('individual') }
                    ]
                },
                {
                    key: 'status',
                    label: 'Status',
                    options: [
                        { id: 'konfiguracja', name: 'Konfiguracja' },
                        { id: 'weryfikacja', name: 'Weryfikacja' },
                        { id: 'w realizacji', name: 'W realizacji' },
                        { id: 'zrealizowane', name: 'Zrealizowane' }
                    ]
                }
            ]" :sort-options="[
                { key: 'created_at', label: 'Data' },
                { key: 'email', label: 'Email' }
            ]" />

        <div class="order-filters">
            <input type="text" v-model="orderNumberQuery" placeholder="Szukaj po nr zamówienia" class="search-input" />
        </div>

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <div class="status-wrapper">
            <p v-if="orderStore.loading" class="status-loading">Ładowanie...</p>
        </div>

        <div v-if="!orderStore.loading">
            <div v-if="filteredOrders.length" class="order-list">
                <EntityCard v-for="order in paginatedOrders" :key="order.id">
                    <template #content>
                        <div class="order-meta">
                            <span class="order-date">{{ orderStore.formatDate(order.created_at) }}</span>
                        </div>
                        <div class="order-details">
                            <div class="detail-item">
                                <span class="detail-label">Nr zamówienia: </span>
                                <span class="detail-value">{{ order.id }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Klient:</span>
                                <span>{{ orderStore.formatType(order.customer_type) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Email:</span>
                                <a :href="`mailto:${order.email}`" class="detail-value">{{ order.email }}</a>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Telefon:</span>
                                <a :href="`tel:${order.phone}`" class="detail-value">{{ order.phone }}</a>
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <div class="detail-item">
                            <span class="detail-label">Status: </span>
                            <select :value="order.status" @change="updateStatus(order.id, $event.target.value)">
                                <option value="konfiguracja">Konfiguracja</option>
                                <option value="weryfikacja">Weryfikacja</option>
                                <option value="w realizacji">W realizacji</option>
                                <option value="zrealizowane">Zrealizowane</option>
                            </select>
                        </div>
                        <button class="btn btn-view" @click="viewOrder(order.id)">
                            <font-awesome-icon icon="eye" />
                            Szczegóły
                        </button>
                        <button class="btn btn-delete" @click="confirmDelete(order.id)">
                            <font-awesome-icon icon="trash" />
                            Usuń
                        </button>
                    </template>
                </EntityCard>
            </div>

            <div v-else class="empty-state">
                <font-awesome-icon icon="box-open" size="2x" />
                <p>Brak zamówień do wyświetlenia</p>
            </div>
        </div>

        <teleport to="body">
            <transition name="fade">
                <OrderDetails v-if="selectedOrder && selectedOrder.trailer" :order="selectedOrder"
                    @close="selectedOrder = null" />
            </transition>
        </teleport>

        <teleport to="body">
            <ConfirmDialog :visible="confirmDialog.state.isVisible" :title="confirmDialog.state.dialogOptions.title"
                :message="confirmDialog.state.dialogOptions.message"
                :confirmText="confirmDialog.state.dialogOptions.confirmText"
                :cancelText="confirmDialog.state.dialogOptions.cancelText"
                :danger="confirmDialog.state.dialogOptions.danger" @confirm="confirmDialog.handleConfirm"
                @cancel="confirmDialog.handleCancel" @close="confirmDialog.handleClose" />
        </teleport>
    </section>
</template>

<script>
import { useOrderStore } from '@/stores/orderStore'
import { useTrailerStore } from '@/stores/trailerStore'
import { showErrorToast, showSuccessToast } from '@/utils/errorUtils'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import EntityCard from '@/components/cms/EntityCard.vue'
import EntityFilterBar from '@/components/cms/EntityFilterBar.vue'
import OrderDetails from '@/components/cms/OrderDetails.vue'
import PaginationControls from '@/components/cms/PaginationControls.vue'
import ConfirmDialog from '@/components/cms/ConfirmDialog.vue'

export default {
    components: {
        OrderDetails,
        EntityCard,
        EntityFilterBar,
        PaginationControls,
        ConfirmDialog
    },
    setup() {
        const confirmDialog = useConfirmDialog()
        return { confirmDialog }
    },
    data() {
        return {
            orderStore: useOrderStore(),
            trailerStore: useTrailerStore(),
            selectedOrder: null,
            searchQuery: '',
            orderNumberQuery: '',

            filters: {
                customer_type: '',
                status: ''
            },
            sortModel: {
                by: '',
                order: 'asc'
            },
            currentPage: 1,
            itemsPerPage: 50
        }
    },
    computed: {
        filteredOrders() {
            let list = this.orderStore.orders

            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase()
                list = list.filter(order => order.email?.toLowerCase().includes(q))
            }

            if (this.orderNumberQuery) {
                const q = this.orderNumberQuery.toLowerCase()
                list = list.filter(order => order.id.toString().includes(q))
            }

            if (this.filters.status) {
                list = list.filter(order => order.status === this.filters.status)
            }

            if (this.filters.customer_type) {
                list = list.filter(order => order.customer_type === this.filters.customer_type)
            }

            if (this.sortModel.by) {
                list = [...list].sort((a, b) => {
                    const fieldA = a[this.sortModel.by]
                    const fieldB = b[this.sortModel.by]

                    if (fieldA < fieldB) return this.sortModel.order === 'asc' ? -1 : 1
                    if (fieldA > fieldB) return this.sortModel.order === 'asc' ? 1 : -1
                    return 0
                })
            }

            return list
        },
        paginatedOrders() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            return this.filteredOrders.slice(start, start + this.itemsPerPage)
        },
        totalPages() {
            return Math.ceil(this.filteredOrders.length / this.itemsPerPage)
        }
    },
    mounted() {
        if (this.orderStore.orders.length === 0) {
            this.orderStore.fetchOrders()
        }
    },
    methods: {
        viewOrder(id) {
            const order = this.filteredOrders.find(o => o.id === id)
            if (!order) return

            const trailer = this.trailerStore.trailers.find(t => t.id === order.trailer_id)
            const addons = this.trailerStore.addons.filter(a => order.addon_ids.includes(a.id))

            const enrichedAddons = addons.map(addon => {
                const price = this.trailerStore.addonsForModels.find(
                    afm => afm.addon_id === addon.id && afm.trailer_id === trailer?.id
                )?.price

                return {
                    ...addon,
                    group: addon.group || 'Inne',
                    price: Math.round(parseFloat(price || 0) * 100)
                }
            })

            this.selectedOrder = {
                ...order,
                trailer,
                addons: enrichedAddons
            }
        },
        async confirmDelete(id) {
            const confirmed = await this.confirmDialog.showConfirm({
                title: 'Usunąć zamówienie?',
                message: 'Ta operacja jest nieodwracalna. Wszystkie dane związane z tym zamówieniem zostaną usunięte.',
                confirmText: 'Tak, usuń',
                cancelText: 'Anuluj',
                danger: true
            })

            if (confirmed) {
                try {
                    await this.orderStore.deleteOrder(id)
                    showSuccessToast('Zamówienie usunięte.')
                } catch (err) {
                    showErrorToast(err, 'Błąd przy usuwaniu zamówienia.')
                }
            }
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        },
        async updateStatus(orderId, newStatus) {
            try {
                await this.orderStore.updateOrder(orderId, { status: newStatus })

                const order = this.orderStore.orders.find(o => o.id === orderId)
                if (order) order.status = newStatus

                showSuccessToast(`Status zamówienia ${orderId} zaktualizowany do "${newStatus}"`)
            } catch (err) {
                console.error(err)
                showErrorToast(err, 'Błąd przy aktualizacji statusu')
            }
        }
    }
}
</script>

<style scoped lang="scss">
.order-section {
    max-width: 1200px;
    margin: 0 auto;
    cursor: default;
}

.section-header {
    margin-bottom: 1rem;
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $color-primary-dark;
    margin: 0;
}

.status-wrapper {
    margin-bottom: 1.5rem;
}

.status-loading {
    font-size: 1rem;
    color: $color-text-light;
    text-align: center;
}

.status-error {
    font-size: 1rem;
    color: $color-primary-accent;
    font-weight: 500;
}

.order-list {
    display: grid;
    gap: 1.25rem;
}

.order-meta {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.order-date {
    font-size: 0.85rem;
    color: $color-text-light;
    font-weight: 500;
}

.order-details {
    display: grid;
    gap: 0.5rem;
}

.search-input {
    padding: 0.5rem 0.4rem;
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.95rem;
    color: $color-text;
    background-color: $color-white;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
        outline: none;
        border-color: $color-primary;
        box-shadow: 0 0 0 2px rgba($color-primary, 0.2);
    }
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
}

.detail-item select {
    padding: 0.2rem 0.4rem;
    border: 1px solid #ccc;
    border-radius: 4px;
}

.detail-label {
    font-weight: 600;
    color: $color-text;
    min-width: 60px;
}

.detail-value {
    color: $color-text;
    text-decoration: none;

    &:hover {
        color: $color-primary;
        text-decoration: underline;
    }
}

.order-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &-view {
        background: $color-primary;
        color: $color-white;

        &:hover {
            background: $color-primary-dark;
        }
    }

    &-delete {
        background: $color-grey-light;
        color: $color-primary-accent;

        &:hover {
            background: $color-primary-accent;
            color: $color-white;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    border: 1px solid $color-grey;

    svg {
        color: $color-grey-dark;
    }

    p {
        color: $color-text-light;
        font-size: 1.1rem;
        margin: 0;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
